import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../components/controls/FormInput";
import Autocomplete from "../../components/Places/PlacesAutocomplete";
import Toggle from "../../components/ui/Toggle";
import Button from "../../components/controls/Button";

export default function RepresentativeProfileForm({
  userId, // ← obligatoire : le Supabase userId du représentant à éditer
  onSaved, // ← callback après sauvegarde OK
  showIntro = false, // ← afficher/masquer les paragraphes d’intro
  showTitle = false, // ← afficher/masquer le H1
  saveButtonLabel, // ← override du libellé du bouton
  compact = true, // ← n’influence que le spacing/typo léger
}) {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    displayName: "",
    location: null,
    phoneNumber: "",
    email: "",
    availability: "",
    code: "",
    city: "",
    isActive: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!userId) return;
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Representative/profile/${userId}`
    )
      .then((res) => res.json())
      .then((data) => setForm((f) => ({ ...f, ...data })))
      .catch(console.error);
  }, [userId]);

  const handleChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!userId) return;
    setLoading(true);
    setSuccess(false);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/profile/${userId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        }
      );
      if (res.ok) {
        setSuccess(true);
        onSaved?.();
      } else {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "Save failed");
      }
    } catch (e) {
      console.error(e);
      // (optionnel) tu peux remonter une erreur UI ici
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`p-6 text-primary border border-ananas rounded-tr-lg rounded-b-lg`}
    >
      {showTitle && (
        <h1 className="text-2xl font-bold mb-4">
          {t("global.myPublicProfile")} {form.code && `(${form.code})`}
        </h1>
      )}

      <a src={`ananassolutions.ca/representant/${form.code}`}></a>

      {showIntro && (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Ces informations ne sont{" "}
            <strong>pas liées à vos produits ou à votre compagnie</strong>.
            Elles serviront à votre représentation et seront{" "}
            <strong>affichées publiquement</strong> pour permettre aux clients
            de vous contacter en cas de besoin. Si vous ne souhaitez pas être
            contacté, vous pouvez désactiver votre profil public à l'aide du
            bouton ci-dessous.
          </p>

          <p className="text-sm text-gray-600 mb-4 border-b pb-4">
            <strong>
              *Pour des raisons de sécurité, nous vous recommandons d’utiliser
              une adresse courriel dédiée à votre rôle de représentant pour
              Ananas Solutions. Cette adresse, utilisée pour l’affichage public
              et les échanges avec les clients, peut être différente de celle
              que vous utilisez pour vous connecter à notre site web.
            </strong>
          </p>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["firstName", "Prénom"],
          ["lastName", "Nom"],
          ["phoneNumber", "Téléphone"],
          ["email", "Courriel"],
        ].map(([key, label]) => (
          <div key={key}>
            <label className="block font-medium mb-1">{label}</label>
            <FormInput
              name={key}
              value={form[key] || ""}
              onChange={(name, value) => handleChange(name, value)}
              placeholder={t(`recruitment.${key}`)}
              showRequiredOrOptional={false}
            />
          </div>
        ))}

        {/* City + Autocomplete (aligne City + Location comme dans ta classe d’origine) */}
        <div>
          <label className="block font-medium mb-1">
            {t("representatives.fields.address")}
          </label>
          <Autocomplete
            value={form.city || ""}
            placeholder={t("stepOne.location.placeholder")}
            onSelect={(e) => {
              handleChange("location", e); // contient lat/lng
              handleChange("city", e.formattedAddress); // ex: "Chambly, QC"
            }}
            initialAddress={form.city || ""}
            showTitle={false}
          />
        </div>

        <div className="flex justify-start items-center mt-4">
          <Toggle
            isEnabled={!!form.isActive}
            onToggle={(e) => handleChange("isActive", e.target.checked)}
            labelOn={t("representatives.visible")}
            labelOff={t("representatives.hidden")}
            colorActive="bg-green-500"
            colorInactive="bg-red-500"
            className="toggle-lg"
            title={t("representatives.visibilityToggle")}
          />
        </div>
      </div>

      <div
        className={`${compact ? "mt-4" : "mt-6"} ${
          compact ? "" : "text-center"
        }`}
      >
        <Button
          type="button"
          onClick={handleSave}
          color="primary"
          size="lg"
          disabled={loading}
        >
          {saveButtonLabel || t("global.save")}
        </Button>

        {success && (
          <p className="text-green-600 mt-2">
            {t("representatives.publicProfile.success")}
          </p>
        )}
      </div>
    </div>
  );
}
